import InfoIcon from '@mui/icons-material/Info'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNLoader from 'components/KNLoader/KNLoader'
import { Role } from 'context/authentication/Role.types'
import { useDriversView } from 'context/DriversViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import { getSearchCondition } from 'global/helpers/filtering'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CreateOrEditDriverDialog from './CreateOrEditDriverDialog'
import DriverFilters from './DriverFilters'
import { driversQueryOptions } from './DriverManager.service'
import DriverTable from './DriverTable'

const DriverManager = (): ReactElement => {
  const { t } = useTranslation()
  const { filters } = useDriversView()
  const driversQuery = useQuery(driversQueryOptions)
  const filteredDriversData = useMemo(
    () =>
      (driversQuery.data ?? []).filter((driver) => {
        const keywordsCondition = getSearchCondition([
          driver.name,
          driver.contactNumber,
          driver.email,
        ], filters.keywords)

        return [keywordsCondition].every((condition: boolean) => condition)
      }),
    [driversQuery.data, filters],
  )
  const createOrEditDriverDialog = useDialog<typeof CreateOrEditDriverDialog>()

  const handleAddClick = useCallback((): void => {
    createOrEditDriverDialog.handleOpen()
    analyticsEvent('polestar_cs_add_driver_button_clicked')
  }, [])

  return (
    <Container maxWidth="xxl">
      {driversQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.driver_manager.loading')}</Typography>
        </KNLoader>
      )}
      {driversQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(driversQuery.error)}</Alert>
      )}
      {driversQuery.isSuccess && (
        <>
          <Paper sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1} mb={1}>
              <DriverFilters />
              <Stack
                spacing={1}
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'start', md: 'center' }}
                mb={2}
              >
                <Typography variant="h6">
                  {t('screens.cs.driver_manager.drivers_count', { count: filteredDriversData.length })}
                </Typography>
                {hasAccess(Role.Editor) && (
                  <Button
                    onClick={handleAddClick}
                    variant="contained"
                    color="secondary"
                    size="small"
                  >
                    {t('screens.cs.driver_manager.card.actions.new')}
                  </Button>
                )}
              </Stack>
              <KNCaption icon={<InfoIcon />}>
                {t('screens.cs.driver_manager.disclaimer')}
              </KNCaption>
            </Stack>
            <DriverTable drivers={filteredDriversData} />
          </Paper>
          {hasAccess(Role.Editor) && (
            <CreateOrEditDriverDialog
              open={createOrEditDriverDialog.isOpen}
              onAction={createOrEditDriverDialog.handleAction}
              onClose={createOrEditDriverDialog.handleClose}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default DriverManager
